import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Grid,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from '../../utils/constants';
import {
  procedureStatusLabel,
  statusChipColor,
  statusLabel,
  trainingRoleLabel,
} from '../../utils/trainingAccess';
import ReadAloudButton from './components/ReadAloudButton';

function ModuleCard({ module, onContinue }) {
  const firstIncomplete = (module.lessons || []).find(
    (lesson) => (lesson.progress?.status || 'not_started') !== 'completed'
  );
  const status = module.status || 'not_started';
  const completed = module.completedLessons || 0;
  const total = module.lessonCount || module.lessons?.length || 0;
  const speakText = [module.name, module.description].filter(Boolean).join('. ');

  return (
    <Paper elevation={1} sx={{ p: 2.5, height: '100%' }}>
      <Stack spacing={1.5} sx={{ height: '100%' }}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="h6" sx={{ fontSize: '1.05rem', fontWeight: 700 }}>
              {module.name}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {module.description}
            </Typography>
          </Box>
          <Stack direction="row" spacing={0.5} alignItems="center">
            <ReadAloudButton text={speakText} label={`Read ${module.name}`} />
            <Chip size="small" label={statusLabel(status)} color={statusChipColor(status)} />
          </Stack>
        </Stack>
        <Typography variant="body2" color="text.secondary">
          {completed} of {total} lessons complete
        </Typography>
        <Box sx={{ flexGrow: 1 }} />
        {firstIncomplete ? (
          <Button
            variant="outlined"
            startIcon={<PlayArrowIcon />}
            onClick={() => onContinue(firstIncomplete.procedureId)}
          >
            Continue module
          </Button>
        ) : total > 0 && status === 'completed' ? (
          <Chip size="small" color="success" label="Module complete" />
        ) : (
          <Button variant="outlined" disabled>
            No lessons
          </Button>
        )}
      </Stack>
    </Paper>
  );
}

function procedureLimitationText(proc) {
  // Learner-facing on-demand list: only mention gaps when the core lesson is unavailable.
  if (proc?.showUnavailableWarning || proc?.primaryTaskAvailable === false) {
    return proc.availabilityMessage
      || 'This lesson describes functionality that is not currently available in the Signal Bridge UI.';
  }
  return null;
}

export default function TrainingPageView({
  loading,
  error,
  curriculum,
  onRefresh,
  onOpenLesson,
  onStartRecommended,
  onDemandQuery,
  onDemandLoading,
  onDemandPlan,
  onDemandError,
  onStartOnDemand,
}) {
  const navigate = useNavigate();
  const [query, setQuery] = React.useState('');

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Paper sx={{ p: 3 }}>
        <Typography color="error" gutterBottom>
          {error}
        </Typography>
        <Button onClick={onRefresh}>Retry</Button>
      </Paper>
    );
  }

  const modules = curriculum?.modules || [];
  const summary = curriculum?.summary || {};
  const resume = curriculum?.resume;
  const recent = (curriculum?.recentLessons || []).slice(0, 5);
  const roleLabel = trainingRoleLabel(curriculum?.rolePath);
  const firstLessonId = curriculum?.nextRecommendedLessonId
    || modules[0]?.lessons?.[0]?.procedureId;
  const isFirstUse = !summary.hasStarted && !resume?.procedureId;
  const allComplete = summary.allComplete;

  return (
    <Stack spacing={3}>
      <Box>
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700 }}>
              Training
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mt: 0.5 }}>
              Learn Signal Bridge step by step with saved progress.
            </Typography>
          </Box>
          <ReadAloudButton
            text="Training. Learn Signal Bridge step by step with saved progress."
            label="Read Training intro"
          />
        </Stack>
      </Box>

      <Paper sx={{ p: 2.5, bgcolor: 'primary.50', border: '1px solid', borderColor: 'primary.100' }}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          spacing={2}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
              <Box>
                <Typography variant="overline" color="primary.main">
                  My Training
                </Typography>
                {isFirstUse ? (
                  <>
                    <Typography variant="h6">Welcome to Signal Bridge Training</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                      Your {roleLabel} path contains {summary.totalModules || modules.length} modules.
                    </Typography>
                  </>
                ) : allComplete ? (
                  <>
                    <Typography variant="h6">Training path complete</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                      You finished every module in your {roleLabel} path. Lessons remain available to review.
                    </Typography>
                  </>
                ) : (
                  <>
                    <Typography variant="h6">
                      {summary.completedModules || 0} of {summary.totalModules || modules.length} modules complete
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Role path: {roleLabel}
                    </Typography>
                  </>
                )}
              </Box>
              <ReadAloudButton
                text={
                  isFirstUse
                    ? `Welcome to Signal Bridge Training. Your ${roleLabel} path contains ${summary.totalModules || modules.length} modules.`
                    : allComplete
                      ? `Training path complete. You finished every module in your ${roleLabel} path. Lessons remain available to review.`
                      : `${summary.completedModules || 0} of ${summary.totalModules || modules.length} modules complete. Role path: ${roleLabel}.`
                }
                label="Read training status"
              />
            </Stack>
          </Box>
          {resume?.procedureId ? (
            <Button
              variant="contained"
              onClick={() => onOpenLesson(resume.procedureId)}
              data-testid="continue-training"
            >
              Continue Training
            </Button>
          ) : isFirstUse && firstLessonId ? (
            <Button
              variant="contained"
              onClick={() => onStartRecommended?.(firstLessonId)}
              data-testid="start-training"
            >
              Start Training
            </Button>
          ) : null}
        </Stack>
      </Paper>

      {resume?.procedureId ? (
        <Paper sx={{ p: 2 }}>
          <Typography variant="overline" color="text.secondary">
            Continue Training
          </Typography>
          <Typography variant="body1" sx={{ mt: 0.5 }}>
            Resume at step {resume.currentStep || 1} of {resume.totalSteps || '?'}.
          </Typography>
          <Button sx={{ mt: 1 }} onClick={() => onOpenLesson(resume.procedureId)}>
            Resume lesson
          </Button>
        </Paper>
      ) : null}

      <Box>
        <Typography variant="h6" sx={{ mb: 0.5 }}>
          Role Path
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          Modules in your {roleLabel} learning path
        </Typography>
        <Grid container spacing={2}>
          {modules.map((module) => (
            <Grid item xs={12} md={6} lg={4} key={module.id}>
              <ModuleCard module={module} onContinue={onOpenLesson} />
            </Grid>
          ))}
        </Grid>
      </Box>

      <Paper sx={{ p: 2.5 }}>
        <Typography variant="overline" color="text.secondary">
          On-Demand Training
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 0.5 }}>
          What would you like to learn?
        </Typography>
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
          Try: Configure after-hours doorphone routing
        </Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
          <TextField
            fullWidth
            size="small"
            placeholder="Teach me how to route a doorphone to security after hours"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            data-testid="on-demand-query"
          />
          <Button
            variant="contained"
            disabled={!query.trim() || onDemandLoading}
            onClick={() => onDemandQuery?.(query.trim())}
          >
            {onDemandLoading ? <CircularProgress size={18} color="inherit" /> : 'Find Training'}
          </Button>
        </Stack>
        {onDemandError ? (
          <Typography color="error" variant="body2" sx={{ mt: 1 }}>
            {onDemandError}
          </Typography>
        ) : null}
        {onDemandPlan?.found ? (
          <Paper variant="outlined" sx={{ p: 2, mt: 2 }} data-testid="on-demand-plan">
            <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="h6">{onDemandPlan.plan?.title}</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  {onDemandPlan.plan?.introduction}
                </Typography>
              </Box>
              <ReadAloudButton
                text={[
                  onDemandPlan.plan?.title,
                  onDemandPlan.plan?.introduction,
                  onDemandPlan.plan?.roleMessage,
                  ...(onDemandPlan.plan?.procedures || []).map((p) => p.title),
                ].filter(Boolean).join('. ')}
                label="Read this training path aloud"
              />
            </Stack>
            <Typography variant="body2" sx={{ mt: 1 }}>
              {onDemandPlan.plan?.lessonCount || onDemandPlan.plan?.procedureIds?.length || 0} lessons in this path
            </Typography>
            {onDemandPlan.plan?.roleMessage ? (
              <Alert severity={onDemandPlan.plan?.canStart === false ? 'warning' : 'info'} sx={{ mt: 1.5 }}>
                {onDemandPlan.plan.roleMessage}
              </Alert>
            ) : null}
            <Stack spacing={1} sx={{ mt: 1.5 }}>
              {(onDemandPlan.plan?.procedures || []).map((proc) => {
                const restricted = (onDemandPlan.plan?.restrictedProcedureIds || []).includes(proc.procedureId);
                const limitation = procedureLimitationText(proc);
                const showStatusChip = proc.showUnavailableWarning === true
                  || (proc.primaryTaskAvailable === false && proc.status && proc.status !== 'SUPPORTED');
                return (
                  <Box key={proc.procedureId}>
                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                      <Typography variant="body2" fontWeight={600}>
                        {proc.title}
                      </Typography>
                      {restricted ? (
                        <Chip size="small" label="Admin path" color="default" />
                      ) : null}
                      {showStatusChip ? (
                        <Chip size="small" label={procedureStatusLabel(proc.learnerStatus || proc.status)} color="warning" variant="outlined" />
                      ) : null}
                    </Stack>
                    {limitation ? (
                      <Typography variant="caption" color="text.secondary" display="block">
                        {limitation}
                      </Typography>
                    ) : null}
                  </Box>
                );
              })}
            </Stack>
            <Button
              sx={{ mt: 2 }}
              variant="contained"
              disabled={onDemandPlan.plan?.canStart === false}
              onClick={() => onStartOnDemand?.(onDemandPlan.plan)}
              data-testid="start-on-demand"
            >
              Start Training
            </Button>
          </Paper>
        ) : null}
      </Paper>

      {recent.length > 0 ? (
        <Box>
          <Typography variant="h6" sx={{ mb: 1 }}>
            Recent Lessons
          </Typography>
          <Stack spacing={1}>
            {recent.map((lesson) => (
              <Paper key={lesson.procedureId} sx={{ p: 1.5 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Box>
                    <Typography variant="body2" fontWeight={600}>
                      {lesson.title || `Procedure ${lesson.procedureId}`}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {statusLabel(lesson.status)}
                    </Typography>
                  </Box>
                  <Button size="small" onClick={() => onOpenLesson(lesson.procedureId)}>
                    Open
                  </Button>
                </Stack>
              </Paper>
            ))}
          </Stack>
        </Box>
      ) : null}

      <Button variant="text" onClick={() => navigate(ROUTES.HOME_LAUNCHER)}>
        Back to Home
      </Button>
    </Stack>
  );
}
